import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

interface WorkflowSectionProps {
  onCTA: () => void;
}

export default function WorkflowSection({ onCTA }: WorkflowSectionProps) {
  const { t } = useTranslation();

  const services = [
    { n: "01", title: t("dyna.workflow.s1t"), body: t("dyna.workflow.s1b") },
    { n: "02", title: t("dyna.workflow.s2t"), body: t("dyna.workflow.s2b") },
    { n: "03", title: t("dyna.workflow.s3t"), body: t("dyna.workflow.s3b") },
    { n: "04", title: t("dyna.workflow.s4t"), body: t("dyna.workflow.s4b") },
    { n: "05", title: t("dyna.workflow.s5t"), body: t("dyna.workflow.s5b") },
    { n: "06", title: t("dyna.workflow.s6t"), body: t("dyna.workflow.s6b") },
  ];

  const steps = [
    { n: "01", t: t("dyna.workflow.p1t"), desc: t("dyna.workflow.p1d") },
    { n: "02", t: t("dyna.workflow.p2t"), desc: t("dyna.workflow.p2d") },
    { n: "03", t: t("dyna.workflow.p3t"), desc: t("dyna.workflow.p3d") },
    { n: "04", t: t("dyna.workflow.p4t"), desc: t("dyna.workflow.p4d") },
  ];

  return (
    <section
      id="services"
      style={{
        background: "var(--quartz-200)",
        padding: "64px var(--site-px)",
        position: "relative", zIndex: 1,
      }}
    >
      <div style={{ maxWidth: "var(--site-max)", margin: "0 auto" }}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          style={{ textAlign: "center", marginBottom: 48 }}
        >
          <h2 style={{
            fontFamily: "Coolvetica, system-ui, sans-serif",
            fontSize: "clamp(32px, 4vw, 48px)", fontWeight: 700,
            letterSpacing: "0.05em", textTransform: "uppercase",
            lineHeight: 1.1, margin: "0 0 12px", color: "var(--nova-600)",
          }}>
            {t("dyna.workflow.heading")}
          </h2>
        </motion.div>

        <div className="dyna-workflow__layout" style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 32, alignItems: "stretch" }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <div className="dyna-workflow__services" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, flex: 1, alignContent: "stretch" }}>
              {services.map((s, i) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  style={{
                    padding: "20px 22px", borderRadius: 16,
                    background: "linear-gradient(180deg, #fff 0%, #faf6ee 100%)",
                    border: "1px solid rgba(47,36,57,0.07)",
                    boxShadow: "0 1px 0 rgba(255,255,255,0.9) inset, 0 4px 16px -6px rgba(135,115,180,0.15)",
                    cursor: "default",
                    display: "flex", flexDirection: "column",
                  }}
                  whileHover={{ y: -3, transition: { duration: 0.2 } }}
                >
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--lavender-600)", marginBottom: 8 }}>
                    {s.n}
                  </div>
                  <div style={{ fontFamily: "Coolvetica, system-ui, sans-serif", fontSize: 20, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--nova-600)", marginBottom: 6 }}>
                    {s.title}
                  </div>
                  <p style={{ fontSize: 13, lineHeight: 1.55, color: "color-mix(in oklab, var(--nova-600) 60%, transparent)", margin: 0 }}>
                    {s.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            style={{
              padding: "28px 32px", borderRadius: 20,
              background: "radial-gradient(ellipse 120% 80% at 50% -10%, var(--color-soft) 0%, transparent 55%), linear-gradient(180deg, var(--dyna-bg-raised) 0%, var(--dyna-bg-elevated) 100%)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 1px 0 rgba(255,255,255,0.16) inset",
            }}
          >
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--lavender-400)", display: "block", marginBottom: 20 }}>
              {t("dyna.workflow.how")}
            </span>

            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {steps.map((s, i) => (
                <div key={s.n} style={{ display: "flex", gap: 16, position: "relative" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: "50%",
                      background: i === 0 ? "var(--lavender-600)" : "var(--ctrl-bg)",
                      border: "1px solid var(--dyna-border-strong)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: "0.1em",
                      color: i === 0 ? "#fff" : "var(--lavender-400)", flexShrink: 0,
                    }}>
                      {s.n}
                    </div>
                    {i < steps.length - 1 && (
                      <div style={{ width: 1, flex: 1, minHeight: 20, background: "linear-gradient(180deg, var(--dyna-border-strong), var(--dyna-border))", margin: "4px 0" }} />
                    )}
                  </div>
                  <div style={{ paddingBottom: i < steps.length - 1 ? 20 : 0 }}>
                    <div style={{ fontFamily: "Coolvetica, system-ui, sans-serif", fontSize: 18, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--quartz-200)", lineHeight: 1, marginBottom: 6, marginTop: 4 }}>
                      {s.t}
                    </div>
                    <p style={{ fontSize: 13, lineHeight: 1.55, color: "var(--lavender-300)", margin: 0, fontFamily: "Geist, system-ui, sans-serif", fontWeight: 300 }}>
                      {s.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 28, paddingTop: 20, borderTop: "1px solid var(--dyna-border)" }}>
              <p style={{ fontSize: 13, color: "var(--lavender-400)", margin: "0 0 14px", fontWeight: 300 }}>
                {t("dyna.workflow.kickoff")}
              </p>
              <button className="dyna-btn dyna-btn--primary" onClick={onCTA} style={{ width: "100%", justifyContent: "center" }}>
                {t("dyna.workflow.cta_btn")} <span className="dyna-arrow">→</span>
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
